import { formatTimeSpanNormal, useLiveCounter } from "@buzzeasy/shared-frontend-utilities";
import { Space, theme, Typography } from "antd";
import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { styled } from "styled-components";
import { routes } from "../../App";
import GraiaIcon from "../../assets/GraiaIcon";
import SideMenu from "../../components/containers/SideMenu";
import CenterContent from "../../components/layouts/CenterContent";
import LayoutWithNav from "../../components/layouts/LayoutWithNav";
import BusinessUnitList from "../../components/presenters/BusinessUnitList";
import FullScreenSpinner from "../../components/presenters/FullScreenSpinner";
import { useGetAgentStateQuery, useGetBusinessUnitsQuery, useGoToAvailableMutation, useUpdateBusinessUnitsMutation } from "../../redux/apis/blenderApi";

const { Title } = Typography;

interface IProps {
  justLoggedIn: boolean;
}

const BusinessUnitsPage: FC<IProps> = ({ justLoggedIn }) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const { data: agentState } = useGetAgentStateQuery();
  const { data: businessUnits, isLoading } = useGetBusinessUnitsQuery();
  const [updateBusinessUnits] = useUpdateBusinessUnitsMutation();
  const [goToAvailable] = useGoToAvailableMutation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const stateSinceInSec = useLiveCounter(agentState?.stateSince ?? new Date());

  const handleSelectBusinessUnit = useCallback(
    (id: string, checked: boolean) => {
      void updateBusinessUnits({ id, enabled: checked });
    },
    [updateBusinessUnits],
  );

  const handleSaveBusinessUnits = useCallback(
    async () => {
      if (justLoggedIn)
        await goToAvailable();

      void navigate(routes.home);
    },
    [goToAvailable, justLoggedIn, navigate],
  );

  return (
    <LayoutWithNav
      displayMenuButton={!justLoggedIn}
      onBusinessUnitsPage={true}
      mainContent={
        <CenterContent $backgroundColor={token.colorPrimaryBg}>
          <Space direction="vertical" size="large" align="center">
            {
              justLoggedIn &&
              <Space direction="vertical" size="middle" align="center">
                <StyledGraiaIcon />
                <Title level={2}>Just logged in</Title>
                <Title level={4}>{formatTimeSpanNormal(Math.max(0, stateSinceInSec))}</Title>
              </Space>
            }
            {
              isLoading
                ? <FullScreenSpinner />
                : <BusinessUnitList
                    businessUnits={businessUnits ?? []}
                    justLoggedIn={justLoggedIn}
                    onBusinessUnitSelect={handleSelectBusinessUnit}
                    onSaveBusinessUnits={handleSaveBusinessUnits}
                  />
            }
          </Space>
        </CenterContent>
      }
      menuContent={justLoggedIn ? null : <SideMenu activeTabKey={routes.businessUnits} onCloseMenu={() => setIsMenuOpen(false)} />}
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    />
  );
};

export default BusinessUnitsPage;

const StyledGraiaIcon = styled(GraiaIcon)`
  width: ${p => p.theme.sizeXXL}px;
  height: ${p => p.theme.sizeXXL}px;
  color: ${p => p.theme.colorPrimary};
`;
